import { useEffect, useMemo, useRef } from "react";

import {
  getFormFlow,
  resetRequestFormAnalyticsState,
  resolveAbandonSectionName,
  setConfirmationStartedAt,
  setFormStartedAt,
  setRequestFormAnalyticsContext,
  trackQuoteConfirmationBackToEditing,
  trackQuoteConfirmationViewed,
  trackQuoteFormEntered,
  trackRouteLeaveAbandon,
  trackTabCloseAbandon,
} from "@RequestFormV2/formEvents/requestFormAnalytics";

type UseRequestFormAnalyticsSessionParams = {
  isEditQuoteRequest?: boolean;
  isEditShipment?: boolean;
  isQuickRequest?: boolean;
  isEditQuickRequest?: boolean;
  isReviewMode: boolean;
  expandedUuids: string[];
};

export function useRequestFormAnalyticsSession({
  isEditQuoteRequest = false,
  isEditShipment = false,
  isQuickRequest = false,
  isEditQuickRequest = false,
  isReviewMode,
  expandedUuids,
}: UseRequestFormAnalyticsSessionParams) {
  const formFlow = useMemo(
    () => getFormFlow({ isEditQuoteRequest, isEditShipment, isQuickRequest, isEditQuickRequest }),
    [isEditQuoteRequest, isEditShipment, isQuickRequest, isEditQuickRequest],
  );
  const expandedUuidsRef = useRef(expandedUuids);
  const isReviewModeRef = useRef(isReviewMode);

  expandedUuidsRef.current = expandedUuids;
  isReviewModeRef.current = isReviewMode;

  useEffect(() => {
    // Module-level session flags must not leak across SPA navigations.
    resetRequestFormAnalyticsState();
    setRequestFormAnalyticsContext({ formFlow });
    setFormStartedAt(Date.now());
    trackQuoteFormEntered();
  }, [formFlow]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      trackTabCloseAbandon(resolveAbandonSectionName(expandedUuidsRef.current, isReviewModeRef.current));
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      trackRouteLeaveAbandon(resolveAbandonSectionName(expandedUuidsRef.current, isReviewModeRef.current));
    };
  }, []);

  useEffect(() => {
    if (!isReviewMode) return;

    setConfirmationStartedAt(Date.now());
    trackQuoteConfirmationViewed();
  }, [isReviewMode]);

  const trackConfirmationBackToEditing = () => {
    trackQuoteConfirmationBackToEditing();
  };

  return { trackConfirmationBackToEditing };
}
